<template>
  <!-- Authenticated: quick access grid -->
  <section class="access-section" v-if="auth">
    <div class="section-container">
      <div class="section-label">{{ authLabel }}</div>
      <h2 class="section-title">{{ authTitle }}</h2>
      <div class="access-grid">
        <router-link
          v-for="(card, i) in authCards"
          :key="i"
          :to="card.link"
          class="access-card"
          :class="card.accent"
        >
          <div class="ac-glow"></div>
          <div class="ac-icon">{{ card.icon }}</div>
          <h3>{{ card.title }}</h3>
          <p>{{ card.desc }}</p>
          <div class="ac-arrow">←</div>
        </router-link>
      </div>
    </div>
  </section>

  <!-- Guest: features grid -->
  <section class="features-section" v-else>
    <div class="section-container">
      <div class="section-label">{{ guestLabel }}</div>
      <h2 class="section-title">{{ guestTitle }}</h2>
      <div class="features-grid">
        <div class="feature-card" v-for="(card, i) in guestCards" :key="i">
          <div class="fc-icon-wrap" :class="card.accent">{{ card.icon }}</div>
          <h3>{{ card.title }}</h3>
          <p>{{ card.desc }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  auth: { type: Boolean, default: false },
  config: { type: Object, default: () => ({}) },
});

const authLabel  = computed(() => props.config.auth_label ?? 'وصول سريع');
const authTitle  = computed(() => props.config.auth_title ?? 'ماذا تريد اليوم؟');
const guestLabel = computed(() => props.config.guest_label ?? 'لماذا منصتنا؟');
const guestTitle = computed(() => props.config.guest_title ?? 'كل ما يحتاجه الطالب في مكان واحد');

const authCards = computed(() => (Array.isArray(props.config.auth_cards) && props.config.auth_cards.length
  ? props.config.auth_cards
  : [
    { icon: '📚', title: 'محتوى الحصص', desc: 'فيديوهات، ملفات PDF، وتسجيلات صوتية', link: '/contents', accent: 'ac-lessons' },
    { icon: '📝', title: 'الامتحانات', desc: 'امتحانات أونلاين مع نتائج فورية', link: '/exams?type=online', accent: 'ac-exams' },
    { icon: '🏠', title: 'الواجب المنزلي', desc: 'واجباتك الدراسية والتدريبات', link: '/exams?type=homework', accent: 'ac-hw' },
    { icon: '📊', title: 'نتائجي', desc: 'تابع تقدمك ومراجعة إجاباتك', link: '/my_exams', accent: 'ac-results' },
  ]));

const guestCards = computed(() => (Array.isArray(props.config.guest_cards) && props.config.guest_cards.length
  ? props.config.guest_cards
  : [
    { icon: '🎬', title: 'دروس فيديو احترافية', desc: 'شرح واضح ومبسط بجودة عالية لجميع المناهج الدراسية', accent: 'fc-purple' },
    { icon: '📝', title: 'امتحانات تفاعلية', desc: 'اختبارات أونلاين مع تصحيح فوري ومراجعة تفصيلية للإجابات', accent: 'fc-blue' },
    { icon: '📊', title: 'متابعة التقدم', desc: 'تتبع أداءك واطلع على نتائجك وتحليل نقاط قوتك وضعفك', accent: 'fc-green' },
    { icon: '💬', title: 'دعم مستمر', desc: 'تواصل مباشر مع الأستاذ وإجابة على كل استفساراتك', accent: 'fc-orange' },
  ]));
</script>

<style scoped>
*,
*::before,
*::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.section-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}
.section-label {
  display: inline-block;
  background: rgba(5, 22, 41, 0.08);
  color: #051629;
  border: 1px solid rgba(5, 22, 41, 0.2);
  border-radius: 99px;
  padding: 0.3rem 1rem;
  font-size: 0.8rem;
  font-weight: 700;
  margin-bottom: 1rem;
}
.section-title {
  font-size: clamp(1.6rem, 3vw, 2.2rem);
  font-weight: 800;
  color: #111827;
  margin-bottom: 2.5rem;
  line-height: 1.3;
}

.access-section,
.features-section {
  padding: 6rem 0;
  background: #f4f7fb;
}

/* Quick access cards */
.access-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}
@media (min-width: 900px) {
  .access-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

.access-card {
  position: relative;
  overflow: hidden;
  border-radius: 24px;
  padding: 2rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  text-decoration: none;
  color: #fff;
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.35s;
  cursor: pointer;
  @media (max-width: 800px) {
    padding: 1rem 1rem;
  }
}
.access-card:hover {
  transform: translateY(-8px) scale(1.02);
  color: #fff;
}
.access-card:hover .ac-glow {
  opacity: 1;
}
.ac-lessons {
  background: linear-gradient(135deg, #051629 0%, #f7b424 100%);
  box-shadow: 0 20px 50px rgba(5, 22, 41, 0.3);
}
.ac-exams {
  background: linear-gradient(135deg, #F7B424 0%, #0066ff 100%);
  box-shadow: 0 20px 50px rgba(0, 194, 255, 0.25);
}
.ac-hw {
  background: linear-gradient(135deg, #00d4a6 0%, #F7B424 100%);
  box-shadow: 0 20px 50px rgba(0, 212, 166, 0.25);
}
.ac-results {
  background: linear-gradient(135deg, #f59e0b 0%, #ef4444 100%);
  box-shadow: 0 20px 50px rgba(245, 158, 11, 0.25);
}
.ac-glow {
  position: absolute;
  inset: 0;
  opacity: 0;
  background: rgba(255, 255, 255, 0.08);
  transition: opacity 0.3s;
  border-radius: inherit;
}
.ac-icon {
  font-size: 2.5rem;
}
.access-card h3 {
  font-size: 1.2rem;
  font-weight: 800;
  margin: 0;
}
.access-card p {
  font-size: 0.88rem;
  opacity: 0.88;
  margin: 0;
  line-height: 1.5;
}
.ac-arrow {
  font-size: 1.2rem;
  margin-top: auto;
  opacity: 0.6;
  transition: transform 0.25s, opacity 0.25s;
}
.access-card:hover .ac-arrow {
  transform: translateX(-6px);
  opacity: 1;
}

/* Feature cards */
.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 1.5rem;
}
.feature-card {
  background: #fff;
  border-radius: 20px;
  padding: 2rem 1.5rem;
  border: 1px solid rgba(5, 22, 41, 0.08);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  transition: all 0.3s;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}
.feature-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 20px 50px rgba(5, 22, 41, 0.12);
  border-color: rgba(5, 22, 41, 0.2);
}
.fc-icon-wrap {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.6rem;
}
.fc-purple {
  background: linear-gradient(
    135deg,
    rgba(5, 22, 41, 0.1),
    rgba(247, 180, 36, 0.1)
  );
}
.fc-blue {
  background: linear-gradient(
    135deg,
    rgba(0, 194, 255, 0.1),
    rgba(0, 102, 255, 0.1)
  );
}
.fc-green {
  background: linear-gradient(
    135deg,
    rgba(0, 212, 166, 0.1),
    rgba(0, 194, 255, 0.1)
  );
}
.fc-orange {
  background: linear-gradient(
    135deg,
    rgba(245, 158, 11, 0.1),
    rgba(239, 68, 68, 0.1)
  );
}
.feature-card h3 {
  font-size: 1.05rem;
  font-weight: 700;
  color: #111827;
}
.feature-card p {
  font-size: 0.88rem;
  color: #6b7280;
  line-height: 1.7;
}

@media (max-width: 600px) {
  .access-grid {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
